<div class="system-messages">
  <nz-card nzTitle="Create System Message Rule" class="system-messages__card">
    <p class="system-messages__intro">
      Configure automated alerts that will be evaluated every five minutes. Matching users will receive the
      message at the time slot you specify.
    </p>

    <form nz-form [formGroup]="form" (ngSubmit)="saveRule()">
      <nz-row nzGutter="16" class="form-grid">
        <nz-col [nzXs]="24" [nzSm]="12" [nzMd]="8" [nzLg]="8" [nzXl]="6">
          <nz-form-item>
            <nz-form-label [nzFor]="'metricType'" nzRequired>Metric</nz-form-label>
            <nz-form-control nzErrorTip="Please select a metric">
              <nz-select id="metricType" formControlName="metricType" nzPlaceHolder="Select metric">
                <nz-option *ngFor="let option of metricOptions" [nzLabel]="option.label"
                  [nzValue]="option.value"></nz-option>
              </nz-select>
            </nz-form-control>
          </nz-form-item>
        </nz-col>

        <nz-col [nzXs]="24" [nzSm]="12" [nzMd]="8" [nzLg]="8" [nzXl]="6">
          <nz-form-item>
            <nz-form-label [nzFor]="'comparison'" nzRequired>Condition</nz-form-label>
            <nz-form-control nzErrorTip="Choose a comparison">
              <nz-select id="comparison" formControlName="comparison" nzPlaceHolder="Select condition">
                <nz-option *ngFor="let option of comparisonOptions" [nzLabel]="option.label"
                  [nzValue]="option.value"></nz-option>
              </nz-select>
            </nz-form-control>
          </nz-form-item>
        </nz-col>

        <nz-col [nzXs]="24" [nzSm]="12" [nzMd]="8" [nzLg]="8" [nzXl]="6">
          <nz-form-item>
            <nz-form-label [nzFor]="'providerId'" nzRequired>Provider</nz-form-label>
            <nz-form-control nzErrorTip="Select a provider">
              <nz-select id="providerId" formControlName="providerId" nzPlaceHolder="Select provider" nzShowSearch
                nzAllowClear [nzLoading]="providersLoading" [nzDisabled]="providersLoading || !providerOptions.length">
                <nz-option *ngFor="let provider of providerOptions" [nzLabel]="provider.name" [nzValue]="provider.id">
                </nz-option>
              </nz-select>
            </nz-form-control>
          </nz-form-item>
        </nz-col>

        <nz-col [nzXs]="24" [nzSm]="12" [nzMd]="8" [nzLg]="8" [nzXl]="6">
          <nz-form-item>
            <nz-form-label [nzFor]="'threshold'" nzRequired>Threshold</nz-form-label>
            <nz-form-control nzErrorTip="Provide a target value">
              <nz-input-number id="threshold" formControlName="threshold" [nzMin]="0" [nzPrecision]="0"
                nzPlaceHolder="0"></nz-input-number>
            </nz-form-control>
          </nz-form-item>
        </nz-col>

        <nz-col [nzXs]="24" [nzSm]="24" [nzMd]="12" [nzLg]="12" [nzXl]="12">
          <nz-form-item>
            <nz-form-label [nzFor]="'triggerHour'" nzRequired>
              Delivery Hour
              <span class="timezone-hint">(Mountain Time • Arizona/Utah)</span>
            </nz-form-label>
            <nz-form-control nzErrorTip="Select an hour">
              <nz-select id="triggerHour" formControlName="triggerHour" nzPlaceHolder="Select hour"
                [nzGetContainer]="selectDropdownContainer">
                <nz-option *ngFor="let hour of hourOptions" [nzLabel]="hour.label" [nzValue]="hour.value"></nz-option>
              </nz-select>
            </nz-form-control>
          </nz-form-item>
        </nz-col>
      </nz-row>

      <nz-form-item>
        <nz-form-label [nzFor]="'message'" nzRequired>Message</nz-form-label>
        <nz-form-control nzErrorTip="Enter the message that will be sent">
          <textarea id="message" nz-input formControlName="message" rows="3" nzMaxCharacterCount="500"
            placeholder="Good Job on your installs today..."></textarea>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label [nzFor]="'userIds'" nzRequired>Recipients</nz-form-label>
        <nz-form-control nzErrorTip="Pick at least one user" [nzValidateStatus]="form.controls['userIds']">
          <div class="recipient-picker">
            <nz-input-group nzPrefixIcon="search">
              <input nz-input placeholder="Search users by name" [formControl]="recipientSearchControl"
                [disabled]="savingRule" />
            </nz-input-group>
            <div class="recipient-picker__hint">
              Select one or more recipients. You can combine search and scrolling.
            </div>

            <div class="selected-recipients" *ngIf="selectedRecipients.length">
              <nz-tag nzMode="closeable" *ngFor="let user of selectedRecipients" (nzOnClose)="removeRecipient(user.id)">
                {{ formatUserLabel(user.label) }}
                <span *ngIf="user.company" class="selected-recipients__company"> • {{ user.company }}</span>
              </nz-tag>
            </div>

            <nz-spin [nzSpinning]="loadingUsers">
              <div class="recipient-list-wrapper">
                <app-user-selection-list [users]="recipientListItems" [isSelected]="recipientSelectionChecker"
                  [disabled]="savingRule"
                  (toggleSelection)="toggleRecipient($event.userId, $event.checked)"></app-user-selection-list>
              </div>
              <div class="recipient-list__empty" *ngIf="!recipientListItems.length && !loadingUsers">
                <p>No users match your search.</p>
              </div>
            </nz-spin>
          </div>
        </nz-form-control>
      </nz-form-item>

      <div class="system-messages__actions">
        <button nz-button nzType="default" type="button" *ngIf="editingRuleId" (click)="cancelEditing()"
          [disabled]="savingRule">
          Cancel
        </button>
        <button nz-button nzType="primary" [disabled]="savingRule" [nzLoading]="savingRule" type="submit">
          {{ editingRuleId ? 'Update Rule' : 'Save Rule' }}
        </button>
      </div>
    </form>
  </nz-card>

  <nz-card nzTitle="Scheduled Rules">
    <nz-table *ngIf="rules.length; else emptyState" [nzData]="rules" [nzLoading]="loadingRules" nzSize="middle"
      class="system-messages__table">
      <thead>
        <tr>
          <th>Metric</th>
          <th>Condition</th>
          <th>Provider</th>
          <th>Message</th>
          <th>Recipients</th>
          <th>Creator</th>
          <th>Hour</th>
          <th>Last Run</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let rule of rules">
          <td>{{ rule.metricType | titlecase }}</td>
          <td>{{ rule.comparator }} {{ rule.threshold }}</td>
          <td>{{ getProviderLabel(rule.providerId) }}</td>
          <td>{{ rule.message }}</td>
          <td>
            <div class="rule-recipients">
              <nz-tag *ngFor="let id of rule.userIds">
                {{ getRecipientLabel(id) }}
              </nz-tag>
            </div>
          </td>
          <td>
            <span>{{ rule.creatorId ? getUserLabel(rule.creatorId) : '—' }}</span>
          </td>
          <td>{{ rule.triggerHour }}</td>
          <td>{{ rule.lastRunAt ? (rule.lastRunAt | date:'short') : 'Not yet run' }}</td>
          <td>
            <button nz-button nzType="link" (click)="startEditRule(rule)" [disabled]="savingRule">
              Edit
            </button>
            <button nz-button nzType="link" nzDanger (click)="removeRule(rule)" [disabled]="!rule.id">
              Remove
            </button>
          </td>
        </tr>
      </tbody>
    </nz-table>

    <ng-template #emptyState>
      <nz-empty nzNotFoundContent="No rules yet. Create one above to start sending automated reminders."></nz-empty>
    </ng-template>
  </nz-card>
</div>
